import React, { useEffect, useState } from "react";
import ProgressBar from "react-customizable-progressbar";
import Back from "../../assets/ProgressBarCircular/Back.svg";
import Forward from "../../assets/ProgressBarCircular/Forward.svg";
import ForwardInactive from "../../assets/ProgressBarCircular/ForwardInactive.svg";
import Coffee from "../../assets/ProgressBarCircular/Coffee.svg";

import {
  Checkin,
  CoffeeIcon,
  Container,
  DetailContainer,
  DetailProgress,
  DetailSchedule,
  DetailTitle,
  Heading,
  History,
  Icon,
  Interval,
  ProgressBarContainer,
  Text,
  Title,
  Year,
  YearContainer,
} from "./CheckinProgress.styles";

import { Checkin as CheckinType } from "../../services/api/types/Members";

function toDateStr(date: Date) {
  const dateStrOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  } as const;
  return date.toLocaleDateString(undefined, dateStrOptions).toUpperCase();
}

export const CheckinProgress: React.FC<{
  hireDate: Date;
  checkIns: Array<CheckinType>;
}> = ({ hireDate, checkIns }) => {
  const today = new Date();
  // calculate the date of ninety day review
  const ninetyDate = new Date(
    new Date(hireDate).setDate(hireDate.getDate() + 90)
  );
  const [year, setYear] = useState<number>(today.getFullYear());
  const [mostRecentByYear, setMostRecentByYear] = useState(
    new Map<number, Date>()
  );
  const [checkInYears, setCheckInYears] = useState(
    new Map<number, Array<CheckinType>>()
  );

  // breaks up the checkins array into a map of years
  const splitYears = (checkIns: Array<CheckinType>) => {
    const years = new Map<number, Array<CheckinType>>();
    const mostRecentByYear = new Map<number, Date>();
    for (const checkIn of checkIns) {
      const checkInDate = new Date(
        (checkIn.timePeriod as string).replace("Z", "") //fix to user timezone
      );
      const checkInYear = checkInDate.getFullYear();
      const year = years.get(checkInYear);
      //find the most recent for that year
      let mostRecent = mostRecentByYear.get(checkInYear);
      if (year) {
        year.push(checkIn);
      } else {
        years.set(checkInYear, [checkIn]);
      }
      if (mostRecent && mostRecent > checkInDate) {
        mostRecent = checkInDate;
      } else {
        mostRecentByYear.set(checkInYear, checkInDate);
      }
    }
    setMostRecentByYear(mostRecentByYear);
    setCheckInYears(years);
    setYearCheckInNumbers(years.get(year) || [], year);
    setMostRecent(
      mostRecentByYear.get(year) ||
        new Date(new Date(new Date(hireDate).setFullYear(year)).setMonth(0))
    );
  };

  useEffect(() => {
    splitYears(checkIns);
  }, [checkIns]);

  const [currCheckins, setCurrCheckins] = useState(
    (checkInYears.get(year) || []).length
  );
  //annual check-in should occur on hire date
  //accept hire date as props and add to Total
  const [totalCheckins, setTotalCheckins] = useState<number>(
    today.getMonth() + 1
  );
  const [mostRecent, setMostRecent] = useState<Date>(
    mostRecentByYear.get(year) || new Date()
  );

  const setYearCheckInNumbers = (
    yearCheckins: Array<CheckinType>,
    year: number
  ) => {
    setCurrCheckins(yearCheckins.length);
    //get total for current year or 12 for past year
    let total = year === today.getFullYear() ? today.getMonth() + 1 : 12;
    if (year === ninetyDate.getFullYear()) {
      //if month is after ninetyDate add one
      if (total > ninetyDate.getMonth()) total++;
    } else {
      //if month is after annual add one
      if (total > hireDate.getMonth()) total++;
    }
    setTotalCheckins(total);
    setMostRecent(
      mostRecentByYear.get(year) ||
        new Date(new Date(new Date(hireDate).setFullYear(year)).setMonth(0))
    );
  };

  const onBackClick = () => {
    const newYear = year - 1;
    setYear(newYear);
    //get request
    setYearCheckInNumbers(checkInYears.get(newYear) || [], newYear);
  };

  const onForwardClick = () => {
    const newYear = year + 1;
    if (newYear > today.getFullYear()) {
      return;
    } else {
      setYear(newYear);
      //get request
      setYearCheckInNumbers(checkInYears.get(newYear) || [], newYear);
    }
  };

  return (
    <Container>
      <Heading>
        <Title>Check-ins</Title>
        <YearContainer>
          <Icon src={Back} onClick={onBackClick} />
          <Year>{year}</Year>
          <Icon
            onClick={onForwardClick}
            src={year === new Date().getFullYear() ? ForwardInactive : Forward}
          />
        </YearContainer>
      </Heading>

      <ProgressBarContainer>
        <ProgressBar
          radius={120}
          progress={currCheckins}
          steps={totalCheckins}
          cut={160}
          rotate={170}
          strokeWidth={10}
          strokeColor="#38D9A9"
          trackStrokeWidth={10}
          trackStrokeColor="#C3CBCD"
          pointerRadius={15}
          pointerStrokeWidth={1}
          pointerStrokeColor="#CED7DE"
          initialAnimation
          initialAnimationDelay={2}
          transition=".9s ease"
          trackTransition=".9s ease"
        >
          <DetailContainer>
            <DetailTitle>Check-ins completed</DetailTitle>
            <DetailProgress>
              {currCheckins}/{totalCheckins}
            </DetailProgress>
            {currCheckins / totalCheckins < 0.8 ? (
              <DetailSchedule status="red">Behind Schedule</DetailSchedule>
            ) : currCheckins / totalCheckins < 0.99 ? (
              <DetailSchedule status="yellow">Closing in</DetailSchedule>
            ) : (
              <DetailSchedule status="green">On Schedule</DetailSchedule>
            )}
          </DetailContainer>
        </ProgressBar>
      </ProgressBarContainer>

      <History>
        <Checkin>
          <CoffeeIcon src={Coffee} />
          <Text>Last Checkin</Text>
          <Text>{toDateStr(mostRecent)}</Text>
          <Interval>Monthly</Interval>
        </Checkin>
        <Checkin>
          <CoffeeIcon src={Coffee} />
          <Text green>Next Checkin</Text>
          <Text>
            {toDateStr(
              new Date(new Date(mostRecent).setMonth(mostRecent.getMonth() + 1))
            )}
          </Text>
          <Interval>Monthly</Interval>
        </Checkin>
        <Checkin>
          <CoffeeIcon src={Coffee} />
          <Text green>Next Touchpoint</Text>
          <Text>
            {toDateStr(
              today < ninetyDate
                ? ninetyDate
                : new Date(new Date(hireDate).setFullYear(year))
            )}
          </Text>
          <Interval>{today < ninetyDate ? "Ninety" : "Annual"}</Interval>
        </Checkin>
      </History>
    </Container>
  );
};
